import React from 'react';
import { Box, Image, Container, Heading, Text, VStack, SimpleGrid, Icon, List, ListItem, Badge, HStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';

const MotionBox = motion(Box);

const AIMasteryMastery = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="brand.50">
      <Container maxW="container.xl">
        <VStack spacing={12}>
          {/* Title */}
          <VStack spacing={4} textAlign="center">
            <Heading size="2xl" fontWeight="900" color="brand.800">
              {data.title}
            </Heading>
            {data.subtitle && (
              <Text fontSize="lg" color="brand.600" maxW="3xl">
                {data.subtitle}
              </Text>
            )}
          </VStack>

          {/* Core Skills (New Structure) */}
          {data.coreSkills && (
            <VStack spacing={8} w="full">
              {data.coreSkills.map((skill, index) => (
                <MotionBox
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  w="full"
                >
                  <Box
                    p={8}
                    bg="white"
                    borderRadius="2xl"
                    boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                    _hover={{
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(128,90,213,0.2)',
                    }}
                    transition="all 0.3s"
                  >
                    <VStack spacing={6} align="flex-start">
                      <HStack spacing={4}>
                        <Box p={3} bg="purple.50" borderRadius="xl">
                          <Icon as={skill.icon} boxSize={8} color="purple.600" />
                        </Box>
                        <VStack align="flex-start" spacing={1} flex={1}>
                          <Heading size="md" color="brand.700">
                            {skill.title}
                          </Heading>
                          <Text color="brand.600" fontSize="md">
                            {skill.desc}
                          </Text>
                        </VStack>
                      </HStack>

                      {skill.techniques && (
                        <List spacing={2} w="full">
                          {skill.techniques.map((technique, i) => (
                            <ListItem key={i}>
                              <HStack align="flex-start" spacing={3}>
                                <Icon as={FaCheckCircle} color="accent.green" boxSize={4} mt={1} flexShrink={0} />
                                <Text color="brand.600" fontSize="sm">{technique}</Text>
                              </HStack>
                            </ListItem>
                          ))}
                        </List>
                      )}

                      {skill.impact && (
                        <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
                          {skill.impact}
                        </Badge>
                      )}

                      {skill.visualPlaceholder && (
                        <Box
                          p={4}
                          bg="gray.100"
                          borderRadius="lg"
                          borderStyle="dashed"
                          borderWidth="2px"
                          borderColor="gray.300"
                          w="full"
                        >
                                        <Image src={skill.visualPlaceholder}  />

                        </Box>
                      )}
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </VStack>
          )}

          {/* Prompt Patterns Section */}
          {data.promptPatterns && (
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              w="full"
            >
              <Box p={8} bg="purple.50" borderRadius="2xl" borderTop="6px solid" borderColor="purple.600">
                <VStack spacing={6} align="stretch">
                  <VStack spacing={2} align="flex-start">
                    <Heading size="lg" color="brand.800">
                      {data.promptPatterns.title}
                    </Heading>
                    {data.promptPatterns.subtitle && (
                      <Text color="brand.600" fontSize="md">
                        {data.promptPatterns.subtitle}
                      </Text>
                    )}
                  </VStack>

                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {data.promptPatterns.patterns?.map((pattern, i) => (
                      <Box key={i} p={4} bg="white" borderRadius="lg">
                        <HStack spacing={2}>
                          <Icon as={FaCheckCircle} color="purple.600" boxSize={4} />
                          <Text fontWeight="600" color="brand.700" fontSize="sm">
                            {pattern}
                          </Text>
                        </HStack>
                      </Box>
                    ))}
                  </SimpleGrid>

                  {data.promptPatterns.fullLibrary && (
                    <Badge colorScheme="green" fontSize="md" px={4} py={2} alignSelf="center">
                      {data.promptPatterns.fullLibrary}
                    </Badge>
                  )}
                </VStack>
              </Box>
            </MotionBox>
          )}

          {/* Fallback to old Skills Grid */}
          {data.skills && !data.coreSkills && (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} w="full">
              {data.skills.map((skill, index) => (
                <MotionBox
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Box
                    p={8}
                    bg="white"
                    borderRadius="2xl"
                    boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                    h="full"
                    _hover={{
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(128,90,213,0.2)',
                    }}
                    transition="all 0.3s"
                  >
                    <VStack spacing={4} align="flex-start">
                      <Box p={3} bg="purple.50" borderRadius="xl">
                        <Icon as={skill.icon} boxSize={8} color="purple.600" />
                      </Box>
                      <Heading size="md" color="brand.700">
                        {skill.title}
                      </Heading>
                      <Text color="brand.500" fontSize="sm">
                        {skill.desc}
                      </Text>
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default AIMasteryMastery;
